import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import { ArrowBackIosNew as BackIcon } from '@mui/icons-material';
import { productService } from '../../services/productService';
import toast from 'react-hot-toast';

export const VendorDetail = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  // States
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vendor, setVendor] = useState(null);

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, [vendorId]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      // Fetch all products and filter by vendor on frontend
      const response = await productService.getProducts({
        vendor_id: vendorId,
      });

      if (response.data && response.data.data) {
        setProducts(response.data.data);
        // Extract vendor info from first product if available
        if (response.data.data.length > 0 && response.data.data[0].vendor_name) {
          setVendor({
            id: vendorId,
            name: response.data.data[0].vendor_name,
          });
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
      toast.error(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products by date range
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Check vendor match
      if (product.vendor !== vendorId) return false;

      // If no date filters applied, include product
      if (!startDate && !endDate) return true;

      // Parse product created_at date
      const productDate = new Date(product.created_at);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      // Set end date to end of day
      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      // Filter by date range
      if (start && productDate < start) return false;
      if (end && productDate > end) return false;

      return true;
    });
  }, [products, vendorId, startDate, endDate]);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleRefresh = () => {
    loadProducts();
  };

  return (
    
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100%', width: '100%' }}>
        {/* Header with Back Button */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate('/vendors')}
            size="small"
            sx={{ color: 'primary.main' }}
          >
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {vendor?.name || 'Vendor Products'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredProducts.length} products
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>

        {/* Date Range Filter */}
        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Filter by Purchase Date Range
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
            gap: { xs: 1.5, sm: 2 },
            alignItems: 'flex-end',
          }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Products Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary" variant="body1">
              {products.length === 0
                ? 'No products found for this vendor'
                : 'No products match the selected date range'}
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.10)',
                    },
                    borderRadius: 2,
                  }}
                >
                  {/* Product Image */}
                  {product.image && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={product.image}
                      alt={product.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}

                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, flex: 1 }}>
                    {/* Product Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.name}
                    </Typography>

                    {/* Category */}
                    {product.category_name && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {product.category_name}
                      </Typography>
                    )}

                    {/* Description */}
                    {product.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 1,
                        }}
                      >
                        {product.description}
                      </Typography>
                    )}

                    {/* Chips */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      {product.is_veg && (
                        <Chip label="Vegetarian" size="small" color="success" variant="outlined" />
                      )}
                      {product.is_available ? (
                        <Chip label="Available" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip label="Unavailable" size="small" color="error" variant="outlined" />
                      )}
                    </Box>

                    {/* Created Date */}
                    <Typography variant="caption" color="text.secondary">
                      Added: {new Date(product.created_at).toLocaleDateString('en-IN')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    
  );
};
